import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Typography from '@material-ui/core/Typography'

import { Dialog, UploadButton, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'

import { Tag, CreateTagPayload, CreateTagResponse } from '../../types'
import { createTag } from '../../api'
import { TAG_GROUPS } from '../../consts'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => description.length <= 200,
      message: 'No puede superar los 200 caracteres',
    },
  ],
}

interface CreateTagDialogProps {
  handleClose: () => void
  onDone: (newTag: Tag) => void
}

const CreateTagDialog: React.FC<CreateTagDialogProps> = ({ handleClose, onDone }) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [active, setActive] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const [createTagApi, isLoading] = useApiCall<CreateTagPayload, CreateTagResponse>(createTag)

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setTitle(e.target.value)
  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setDescription(e.target.value)
  const handleActiveChange: React.ChangeEventHandler<HTMLInputElement> = () => setActive(!active)

  const fields = { title, description }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(fields, rules)

  // eslint-disable-next-line no-shadow
  const validateCSV = (file: File): Promise<string | null> => {
    return new Promise(resolve => {
      if (!file.name.endsWith('.csv')) {
        resolve('Solo se permiten archivos .csv')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const text = reader.result as string
        const lines = text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line !== '')
        if (lines.length === 0) {
          resolve('El archivo está vacío.')
          return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const invalidEmails = lines.filter(line => !emailRegex.test(line.trim()))

        if (invalidEmails.length > 0) {
          resolve('El archivo contiene correos inválidos')
          return
        }
        resolve(null)
      }
      reader.onerror = () => {
        resolve('Error al leer el archivo')
      }
      reader.readAsText(file)
    })
  }

  // eslint-disable-next-line no-shadow
  const handleFileChange = async (file: File) => {
    const error = await validateCSV(file)
    setFileError(error)
    if (!error) setFile(file)
    else setFile(null)
  }

  const isSubmitDisabled = isAnyFieldEmpty || hasErrors || isLoading || !file || !!fileError

  const onCreateTag = async () => {
    if (!title || !description) return
    const tag = {
      title,
      description,
      active,
      file,
      group: TAG_GROUPS.FROM_CSV,
    }
    try {
      const { tag: newTag } = await createTagApi(tag)
      onDone(newTag)
    } catch (err) {
      if (err instanceof Error && 'status' in err && err.status === 400) {
        snackbar.show(err.message)
      } else {
        snackbar.show('No se pudo crear la etiqueta. Intente de nuevo.')
      }
    }
  }

  return (
    <Dialog
      title="Nueva etiqueta (tag)"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateTag}
    >
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <TextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.title.hasError}
            helperText={errors.title.message}
            inputProps={{ maxLength: 25 }}
          />
        </Grid>
        <Grid item xs={11}>
          <TextField
            label="Descripción"
            value={description}
            fullWidth
            onChange={handleDescriptionChange}
            disabled={isLoading}
            error={errors.description.hasError}
            helperText={errors.description.message}
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item xs={11}>
          <FormControlLabel
            control={<Checkbox onChange={handleActiveChange} checked={active} />}
            label="Activo"
          />
        </Grid>
        <Grid item xs={11}>
          <UploadButton
            id="csv-upload"
            label="Cargar archivo CSV"
            accept=".csv"
            onChange={handleFileChange}
          />
          {fileError && (
            <Typography color="error" variant="body2">
              {fileError}
            </Typography>
          )}
          {file && !fileError && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Archivo seleccionado: {file.name}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { CreateTagDialog }
